/**
 * Risultati di ricerca.
 *
 * Rispetto all'elenco di una cartella cambia una cosa sola, ed è quella che
 * conta: ogni riga dice **dove sta** il documento. Senza, un elenco di dieci
 * `fattura.pdf` è inutilizzabile.
 *
 * Il percorso è cliccabile e porta alla cartella: trovare un file serve quasi
 * sempre a scoprire dov'era, non solo a scaricarlo.
 *
 * Vale sia per la grafica classica sia per le altre: i risultati non hanno una
 * variante propria, perché il problema che risolvono è lo stesso.
 */
import { api } from '../lib/api.js';
import { formatDate, formatSize } from '../lib/format.js';
import { isShortcut } from '../lib/shortcut.js';
import type { SearchResult } from '../lib/types.js';
import { FileChip, FolderChip } from './FileChip.jsx';
import './SearchResults.css';

export function SearchResults({
  query,
  results,
  truncated,
  clientName,
  onOpenFolder,
  onOpenLink,
}: {
  query: string;
  results: SearchResult[];
  truncated: boolean;
  /** Nome dell'area, mostrato per i risultati che stanno nella radice. */
  clientName: string;
  onOpenFolder: (ref: string) => void;
  /** Collegamenti `.url`: si aprono anche da qui, come nell'elenco. */
  onOpenLink: (entry: SearchResult) => void;
}) {
  if (results.length === 0) {
    return (
      <div className="sres__empty">
        <p>
          Nessun documento trovato per <strong>«{query}»</strong>.
        </p>
        <p className="sres__note">{INDEX_NOTE}</p>
      </div>
    );
  }

  return (
    <div className="sres">
      <p className="sres__count">
        {results.length} {results.length === 1 ? 'risultato' : 'risultati'} per{' '}
        <strong>«{query}»</strong>
        {truncated ? <span className="sres__more"> — mostrati i primi, restringi i termini</span> : null}
      </p>

      <ul className="rows sres__list">
        {results.map((r) => (
          <li key={r.ref} className="row sres__row">
            {r.kind === 'folder' ? <FolderChip /> : <FileChip name={r.name} />}

            <span className="sres__main">
              {r.kind === 'folder' ? (
                <button
                  type="button"
                  className="sres__name sres__name--folder"
                  onClick={() => onOpenFolder(r.ref)}
                  title={r.name}
                >
                  {r.name}
                </button>
              ) : isShortcut(r.name) && r.perms.download ? (
                <button
                  type="button"
                  className="sres__name sres__name--folder"
                  onClick={() => onOpenLink(r)}
                  title={`Apri il collegamento ${r.name}`}
                >
                  {r.name}
                </button>
              ) : (
                <span className="sres__name" title={r.name}>
                  {r.name}
                </span>
              )}

              <button
                type="button"
                className="sres__where"
                onClick={() => onOpenFolder(r.folderRef)}
                title="Apri la cartella"
              >
                {r.folderPath === '' ? clientName : r.folderPath.replaceAll('/', ' › ')}
              </button>
            </span>

            <span className="row__size">{r.kind === 'folder' ? '' : formatSize(r.size)}</span>
            <span className="row__date">{formatDate(r.modifiedAt)}</span>
            <span className="row__actions">
              {r.kind === 'file' && r.perms.download ? (
                isShortcut(r.name) ? (
                  <button type="button" className="row__download" onClick={() => onOpenLink(r)}>
                    <IconExternal />
                    <span>Apri</span>
                  </button>
                ) : (
                  <a className="row__download" href={api.fileUrl(r.ref)} download={r.name}>
                    <IconDownload />
                    <span>Scarica</span>
                  </a>
                )
              ) : null}
            </span>
          </li>
        ))}
      </ul>

      <p className="sres__note">{INDEX_NOTE}</p>
    </div>
  );
}

/**
 * Va detto, non lasciato scoprire: si cerca nei **nomi**, non dentro i file.
 * Chi si aspetta il contrario cerca una parola letta in una fattura, non trova
 * niente e conclude che la ricerca è rotta.
 */
const INDEX_NOTE = 'La ricerca guarda il nome dei documenti e delle cartelle, non il loro contenuto.';

function IconDownload() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
      <path
        d="M12 4v10m0 0 4-4m-4 4-4-4M5 18h14"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconExternal() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14 4h6v6M20 4l-8 8M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
